import React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';

import { Link } from 'core/routing';

import { media, hexa } from 'legacyComponents/styled/styleUtils';
import { CardIndicator } from 'legacyComponents/cards/types/indicator';
import {
  PostCard,
  TextWrapper,
  FullWidthTitle as Title,
  PostCardActions,
} from 'legacyComponents/cards/styles';
import { Avatar, Username } from 'apps/components/shared';
import { Date } from 'apps/components/date';

import { ContentLink } from 'apps/utils';
import { clientStore } from 'apps/client/app';
import PropTypes from 'prop-types';
import { Badge, Button, Paragraph, Tooltip } from 'components';
import { Commenters } from 'apps/components/commenters';
import { imageSrc } from 'apps/file/utils';
import { conf as mainConf } from 'apps/config.yaml';

import { withNamespaces, Trans } from 'react-i18next';

export const OpenProjectIssueCard = withNamespaces()(
  observer(({ cardObject, cardNo, cardColumns }) => (
    <ProjectIssueCard
      cardObject={cardObject}
      cardNo={cardNo}
      cardColumns={cardColumns}
      filter='Open'
    />
  )),
);

export const ClosedProjectIssueCard = withNamespaces()(
  observer(({ cardObject, cardNo, cardColumns }) => (
    <ProjectIssueCard
      cardObject={cardObject}
      cardNo={cardNo}
      cardColumns={cardColumns}
      filter='Closed'
    />
  )),
);

export const ProjectIssueCard = withNamespaces()(
  observer(({ cardObject, cardNo, cardColumns, filter }) => {
    const noMargin = (cardNo + 1) % cardColumns === 0;
    if (cardObject === null) {
      return <CardIndicator noMargin={noMargin} type='issue' />;
    }

    // NOTE: Only render issue card if status matches active tab
    const { status } = cardObject;
    if (status !== filter) {
      return null;
    }

    const {
      number,
      title,
      slug,
      creatorProfile,
      commentsCount,
      dateCreated,
      labels,
      commenters,
    } = cardObject;
    const managerVariables = cardObject.manager.variables;
    const projectSlug = managerVariables.slug;
    const creatorUsername = creatorProfile.username;

    const blockLabels = [];
    labels.forEach(label => {
      blockLabels.push(
        <BadgeStyled
          key={`badge-${label.id}`}
          text={label.name}
          size='xsmall'
          customBgColor={`#${label.color}`}
        />,
      );
    });
    return (
      <PostCard>
        <Link
          name='profile'
          params={{ profile: creatorProfile.username }}
          content={creatorProfile.content}
        >
          <Avatar
            src={imageSrc(
              creatorProfile.avatar,
              creatorProfile.imageFallbackPath,
              mainConf.imgSizes.profile.small,
            )}
          />
        </Link>
        <TextWrapper>
          <Tooltip delayShow={500} />
          <ContentLink
            contentSubPath='issue'
            contentObj={cardObject}
            params={{ slug: projectSlug, issueSlug: slug }}
          >
            <Title
              truncate={
                title.length > 100 &&
                ['default', 'l'].includes(clientStore.activeBreakPoint)
              }
            >
              {title}
            </Title>
          </ContentLink>
          <Paragraph size='small'>
            <Trans>
              #{{ number }} opened by{' '}
              <Username
                name='profile'
                params={{ profile: creatorUsername }}
                content={creatorProfile.content}
              >
                {{ creatorUsername }}
              </Username>
            </Trans>
            <DateStyled value={dateCreated} />
            <ContentLink
              contentSubPath='issue'
              contentObj={cardObject}
              params={{ slug: projectSlug, issueSlug: slug }}
            >
              <Discuss>
                {commentsCount > 0 ? (
                  <Trans count={commentsCount}>
                    {{ commentsCount }} comment
                  </Trans>
                ) : (
                  <Trans>Discuss</Trans>
                )}
              </Discuss>
            </ContentLink>
          </Paragraph>
          {blockLabels}
        </TextWrapper>
        {commenters && !!commenters.length && (
          <CommentersStyled commenters={commenters} />
        )}
        <PostCardActions>
          <ContentLink
            contentSubPath='issue'
            contentObj={cardObject}
            params={{ slug: projectSlug, issueSlug: slug }}
          >
            <ButtonStyled
              variant='secondary'
              icon='chat_multiple'
              data-tip='See comments'
              outline
            />
          </ContentLink>
        </PostCardActions>
      </PostCard>
    );
  }),
);

const CommentersStyled = styled(Commenters)`
  margin-left: auto;
  margin-right: ${props => props.theme.spacing.xsmall};
`;

const ButtonStyled = styled(Button)`
  ${media.s`
    display: none;
  `};
`;

const Discuss = styled.span`
  color: ${props => props.theme.color.wfGrey700};
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    color: ${props => props.theme.color.wfGrey};
  }
`;

const BadgeStyled = styled(Badge)`
  margin-right: ${props => props.theme.spacing.small};
  margin-top: ${props => props.theme.spacing.small};
  border: 1px solid ${props => props.customBgColor};
  background-color: ${props => hexa(props.customBgColor, 0.1)};
  color: ${props => props.customBgColor};
`;

const DateStyled = styled(Date)`
  &::before,
  &::after {
    content: '|';
    display: inline-block;
    margin: 0 ${props => props.theme.spacing.small};
    color: ${props => props.theme.color.wfGrey700};
  }
`;

ProjectIssueCard.propTypes = {
  cardObject: PropTypes.object,
  cardNo: PropTypes.number,
  status: PropTypes.string,
  title: PropTypes.string,
  id: PropTypes.string,
  author: PropTypes.string,
  datePublished: PropTypes.string,
  labels: PropTypes.array,
  comments: PropTypes.array,
};
